import { LocalizedStrings } from 'react-localization';
import { IServiceAdvisor, IHybridTimeTable, IAppointment } from '../../API/GoMoto';
import { Dispatch } from 'react';
import { Dayjs } from 'dayjs';
import { Locales } from '../Localization/localization.types';
import { ActionMap, GoMotoFunction } from '../../API/GoMoto.types';
import {
    Country,
    DayOfWeek,
    DealerStatus,
    DMSType,
    KioskTypeName,
    LoanerOrRentalOption,
    PaymentProvider,
    Timezone,
    ValuationScreen,
    MobileValuationScreen,
    BlackbookAdjustmentMethod,
} from '../../API/Enums';

// #region Dealer Object
export interface Dealer extends DealerLocalized, DealerLocalizedAltLang {
    // V3 Keys
    key: string;
    name: string;
    country: Country;
    city: string;
    street: string;
    stateOrProvince: string;
    postalCode: string;
    phone: string;
    dmsType: DMSType;
    vehicleRuleSetting: string;
    includeCurrentYear: boolean;
    make: string;
    defaultCommentOpCode: string;
    primaryLanguage: Locales;
    alternateLanguage: Locales;
    theme: string;
    themeBannerEnabled: boolean;
    timezone: keyof typeof Timezone;
    vinEnabled: boolean;
    vinRequired: boolean;
    surveyEnabled: boolean;
    newAppointmentEnabled: boolean;
    oldAppointmentEnabled: boolean;
    newCustomerEnabled: boolean;
    tagNumberEnabled: boolean;
    tagNumberRequired: boolean;
    mobileCheckInEnabled: boolean;
    valuationPageEnabled: boolean;
    transportationPageEnabled: boolean;
    blankInitialMileage: boolean;
    website: string;

    /**
     * If the dealer is associated with any Express or 365 kiosks
     */
    hasExpressOr365: boolean;

    hasPhysicalKiosk: boolean;

    // Online Payments
    onlinePaymentsEnabled: boolean;
    readonly onlinePaymentsActive: boolean;
    paymentAccount: string;
    paymentAccountStatus: string | null;
    paymentProvider: PaymentProvider;

    // Services
    straightToServicesForAppointments: boolean;
    serviceEditingEnabled: boolean;
    serviceCommentsEnabled: boolean;
    showDmsServiceLineEstimates: boolean;
    maintenanceMenusEnabled: boolean;
    recommendedNotDoneEnabled: boolean;

    // Upsell
    customUpsellPageEnabled: boolean;
    customUpsellOpCode: string;
    customUpsellName: string;

    // Hunter
    hunterAlignmentOpCode: string;
    hunterAlignmentEnabled: boolean;
    hunterTireTreadOpCode: string;
    hunterTireTreadEnabled: boolean;

    // Recalls
    recallType: string;
    readonly recallsActive: boolean;

    // Xtime
    xtimeEnabled: boolean;
    showXtimeSingleServicePrices: boolean;

    // Valuation
    exclusiveValuationPage: boolean;
    valuationFullScreen: boolean;
    valuationFullScreenImage: string;
    valuationScreen: ValuationScreen;
    mobileValuationScreen?: MobileValuationScreen;
    blackbookAdjustmentMethod?: BlackbookAdjustmentMethod;
    blackbookAdjustment?: number;
    blackbookMinimumValuation?: number;

    // Transportation
    vehiclePickupEnabled: boolean;
    vehiclePickupInstructionsOpCode: string;

    // Service Advisor
    serviceAdvisorEnabled: boolean;
    advisorOptionEnabled: boolean;

    // Dealer options
    facilityId: string;

    // Disclaimers and Disclosures
    consentToSignElectronicallyEnabled: boolean;
    workAuthorizationEnabled: boolean;
    preliminaryEstimateEnabled: boolean;
    retainOldPartsEnabled: boolean;
    roBackerEnabled: boolean;
    stateEstimateEnabled: boolean;
    miscellaneousEnabled: boolean;
    roBackerAcknowledgmentRequired: boolean;
    roBackerReviewRequired: boolean;
    roBackerImage: string | undefined;
    roBackerImageAltLang: string | undefined;
    stateEstimateMinimum: number;
    safeHarborWarningEnabled: boolean;

    // Tow-In/Tow Driver Support
    vehicleTowInOption: boolean;

    // DMS appointment check-in offset
    dmsAppointmentOffsetMinutes?: number;

    // Reminder email settings
    reminderEmailSettings?: ReminderEmailSettings;

    // Texting
    textingPhoneUS?: string;
    textingPhoneCA?: string;
    /**
     * Whether the customer texting dealer setting is enabled regardless of its text status. Most of the time {@link customerTextingActive} is what should be used
     */
    customerTextingEnabled: boolean;
    /**
     * The KeyTrak System ID if the dealer is configured with this integration.
     */
    keyTrakSystemId?: string;
    readonly textStatus?: string;
    /**
     * Whether customer texting is enabled and text status is active
     */
    readonly customerTextingActive: boolean;

    readonly loanerOrRentalOption: LoanerOrRentalOption;
    /**
     * The current status of a Dealer's installation
     */
    readonly status: DealerStatus;
}

export interface DealerLocalized {
    vehiclePickupExtraText: string;
    upsellDisclaimer: string;
    customUpsellPageText: string;
    disclaimerExtra: string;
    echoPageReminderText: string;
    arrivalInstructions: string;
    fullKioskInstructions: string;
    consentToSignMessage: string;
    declineConsentToSignMessage: string;
    workAuthorizationDisclaimer: string;
    retainOldPartsAdditionalText: string;
    miscellaneousDisclaimer: string;
}

export interface DealerLocalizedAltLang {
    vehiclePickupExtraTextAltLang: string;
    upsellDisclaimerAltLang: string;
    customUpsellPageTextAltLang: string;
    disclaimerExtraAltLang: string;
    echoPageReminderTextAltLang: string;
    arrivalInstructionsAltLang: string;
    fullKioskInstructionsAltLang: string;
    consentToSignMessageAltLang: string;
    declineConsentToSignMessageAltLang: string;
    workAuthorizationDisclaimerAltLang: string;
    retainOldPartsAdditionalTextAltLang: string;
    miscellaneousDisclaimerAltLang: string;
}

export interface ReminderHours {
    dayOfWeek: DayOfWeek;
    openTime: string;
    closeTime: string;
}

export interface ReminderEmailSettings {
    logo: string;
    hoursBeforeAppointment: number;
    senderAlias: string;
    replyTo: string;
    message: string;
    signature: string;
    hours: ReminderHours[];
}
// #endregion

// #region Kiosk Object
export type KioskType = 'vsa2' | 'vsa3' | 'vsa4' | 'hybrid' | 'ssk' | 'outdoor' | 'express' | 'mobile';
export type KioskKeyStorageType = 'keydrop' | 'locker';

export interface Kiosk extends KioskLocalized, KioskLocalizedAltLang {
    key: string;
    dealer: Dealer;
    kioskType: KioskType;
    kioskTypeName: KioskTypeName;
    isMultiMake: boolean;
    keyStorageType?: KioskKeyStorageType;
    name: string;
    timeout?: number;
    machineId: string;
    templateFolder: string;
    keyPickupOption: boolean;
}

export interface KioskLocalized {
    disclaimer: string;
    otherKioskErrorMessage: string;
    hybridErrorMessage: string;
    location: string;
}

export interface KioskLocalizedAltLang {
    disclaimerAltLang: string;
    otherKioskErrorMessageAltLang: string;
    hybridErrorMessageAltLang: string;
    locationAltLang: string;
}
// #endregion

export enum KioskDispatchTypes {
    SetLocale = 'SET_LOCALE',
    SetDealer = 'SET_DEALER',
    SetDefaultServiceAdvisor = 'SET_DEFAULT_SERVICE_ADVSIOR',
    SetPossibleDealers = 'SET_POSSIBLE_DEALERS',
    ClearPossibleDealers = 'CLEAR_POSSIBLE_DEALERS',
    RefetchKiosk = 'REFETCH_KIOSK',
    SetMonochromeTheme = 'SET_MONOCHROME_THEME',
    ClearMonochromeTheme = 'CLEAR_MONOCHROME_THEME',
    SetNoAnimations = 'SET_NO_ANIMATIONS',
    ClearNoAnimations = 'CLEAR_NO_ANIMATIONS',
    Initialize = 'INITIALIZE'
}

export type KioskPayload = {
    [KioskDispatchTypes.SetLocale]: Locales;
    [KioskDispatchTypes.SetDealer]: Dealer;
    [KioskDispatchTypes.SetDefaultServiceAdvisor]: IServiceAdvisor;
    [KioskDispatchTypes.SetPossibleDealers]: string[];
    [KioskDispatchTypes.ClearPossibleDealers]: never;
    [KioskDispatchTypes.RefetchKiosk]: never;
    [KioskDispatchTypes.SetMonochromeTheme]: never;
    [KioskDispatchTypes.ClearMonochromeTheme]: never;
    [KioskDispatchTypes.SetNoAnimations]: never;
    [KioskDispatchTypes.ClearNoAnimations]: never;
    [KioskDispatchTypes.Initialize]: KioskContextState;
};

export type KioskActions = ActionMap<KioskPayload>[keyof ActionMap<KioskPayload>];

export interface KioskContextState {
    kiosk: Omit<Kiosk, keyof KioskLocalized | keyof KioskLocalizedAltLang | 'dealer'> & { localization: LocalizedStrings<KioskLocalized>; };
    dealer: Omit<Dealer, keyof DealerLocalized | keyof DealerLocalizedAltLang> & { localization: LocalizedStrings<DealerLocalized>; };
    additionalDealers: Dealer[];
    possibleDealers: string[] | undefined;
    /**
     * Returns the full hours for the kiosk
     */
    hours: IHybridTimeTable[];
    locale: Locales;
    bannerImages: string[];
    defaultServiceAdvisor: IServiceAdvisor | undefined;
    monochromeTheme: boolean;
    noAnimations: boolean;
}

export type UseKioskStateReturn = KioskContextState['kiosk'] & Pick<KioskContextState, 'dealer'>;

/**
 * Type definition for the return object that the `useKioskTimeTable` hook provides.
 *
 * @extends KioskContextState - {@link KioskContextState} description.
 */
export interface KioskTimeTableReturn extends Pick<KioskContextState, 'hours'> {
    /**
     * Returns whether the appointment time occurs after the current relative time with the DMS offset included.
     */
    getIsAppointmentTimeAfterOffset: GoMotoFunction<
        [IAppointment['appointmentDateTime']],
        boolean
    >;
    /**
     * Returns whether the kiosk is closed.
     */
    getIsKioskActuallyClosed: GoMotoFunction<
        [void],
        boolean
    >;
    /**
     * Returns whether the kiosk will be closed within the dealer's DMS appointment offset buffer.
     */
    getWillKioskBeClosed: GoMotoFunction<
        [void],
        boolean
    >;
    /**
     * Computes the next available check in time or null if the kiosk is open or does not have hours available.
     */
    computeNextAvailableCheckinTime: GoMotoFunction<
        [boolean?],
        Dayjs
    >;
}

/**
 * This is the returned object for the `useKioskTimeTable` internal `getKioskOpenHours` function.
 */
export interface KioskOpenHours {
    /**
     * The current relative time.
     */
    currentTime: Dayjs;
    /**
     * The current relative day of the week.
     */
    currentDay: number;
    /**
     * The relative time that the kiosk being used is considered open.
     */
    startTime: Dayjs | null;
    /**
     * The relative time that the kiosk being used is considered closed.
     */
    endTime: Dayjs | null;
    /**
     * When true, the kiosk is considered to be closed for the day.
     */
    isServiceLaneClosed: boolean;
}

export type KioskImageType =
    'KeyPickUp'
    | 'KeyDrop'
    | 'PaymentPop'
    | 'ScanQRCode'
    | 'LicenseScan'
    | 'KeyDropWarning'
    | 'RetrieveKey'
    | 'LicenseScanBack'
    | 'PhoneQR'
    | 'Email'
    | 'Arrow'
    | 'Text';

export type KioskReducerFunction = (state: KioskContextState, action: KioskActions) => KioskContextState;
export type UseKioskStateFunction = () => UseKioskStateReturn;
export type UseKioskFullStateFunction = () => KioskContextState;
export type UseKioskDispatchFunction = () => Dispatch<KioskActions>;
export type UseAdditionalDealersFunction = () => Dealer[];
export type UsePossibleDealersFunction = () => string[] | undefined;
export type UseDefaultServiceAdvisorFunction = () => IServiceAdvisor | undefined;
export type UseMonochromeThemeFunction = () => boolean;
export type UseNoAnimationsFunction = () => boolean;
export type UseTowInSupportFunction = () => boolean;
export type UseSwitchDealerFunction = () => (dealer: Dealer) => void;
/**
 * `useKioskTimeTable` hook type definition.
 *
 * Refer to {@link KioskTimeTableReturn} for information regarding the return object.
 */
export type KioskTimeTableHook = GoMotoFunction<
    [void],
    KioskTimeTableReturn
>;
export type UseFullResetFunction = (redirect?: boolean, abandoned?: boolean) => () => void;
export type UseGetKioskRetrievalParamsFunction = () => KioskRetrievalParams;
export type GenerateKioskLocalizationFunction = (kiosk: Kiosk) => LocalizedStrings<KioskLocalized>;
export type GenerateDealerLocalizationFunction = (dealer: Dealer) => LocalizedStrings<DealerLocalized>;
export type UseKioskLocalizationFunction = () => KioskLocalizationKeys;
export type UseKioskImageFunction = (image: KioskImageType) => string;
export type UseKioskBannerImagesFunction = () => string[];
export type UseArePaymentsEnabledFunction = () => () => Promise<boolean>;


export interface KioskProviderProps {
    children: React.ReactNode;
}

export interface KioskRetrievalParams {
    kioskKey: string;
    machineId: string;
    subdomain: string;
}

export interface KioskLocalizationKeys {
    kiosk: KioskLocalized;
    dealer: DealerLocalized;
}